/**
 * Storyboards: the presentation as data.
 *
 * A storyboard is the same brief a hosted deck shows - the script, its lines,
 * the shots each line cuts to, every start frame and clip with the prompt that
 * made it, the voiceovers - as structured data the platform can draw itself and
 * an agent can read back and edit. These are the shapes both the agent API and
 * the portal API return, so a renderer written against one reads the other.
 *
 * Vocabulary, fixed:
 *  - a **line** is one spoken sentence (`l01`); hooks 2 and 3 are lines of kind
 *    `hook` with `variantOf` naming the line they replace;
 *  - a **shot** is one stretch of picture between two cuts (`s01`), numbered
 *    through the whole ad; a line holds any number of shots;
 *  - a **slot** is a place one piece of media goes - frame option `v1`..`vN`, the
 *    clip, a line's voiceover take, the whole-ad voiceovers - and holds the
 *    prompt that fills it now;
 *  - a **take** is one immutable rendering of a slot: bytes plus the exact
 *    prompt and settings that produced them. Re-rendering adds a take.
 *
 * Counts are never fixed: a frames-only brief has no clip slots, a dialogue ad
 * no voiceover slots, and a new format is a new `kind` string. Every level has a
 * `meta` object for what a format needs and these types do not name.
 *
 * Schema v2 (`schemaVersion: 2`) adds, inside the same tables, everything a
 * generation was made from and everything an editor is handed: the reference
 * images as slots of kind `ref_image`, a typed `inputs[]` on every frame and
 * clip slot naming which slot's bytes played which role, typed
 * `VoiceoverParams` with one voiceover slot per voice alternative on a line,
 * derivative `files` (thumb, poster, web) on a take, the strategist's note on
 * a line, the published revision the editors' deck renders, and a timeline
 * derived from durations. Every v2 field is optional on input, so a v1 body
 * keeps publishing; a reader treats a missing field as "unknown", never as an
 * error. The contract is `docs/presentation-editor/02-storyboard-v2-contract.md`.
 */
import type { StoryboardTimeline } from "./storyboard-timeline.js";
import type { ConceptProduct } from "./pipeline.js";
import type { StoryboardSlotTrim } from "./clip-editor.js";

export const STORYBOARD_STATUSES = ["draft", "ready", "archived"] as const;
export type StoryboardStatus = (typeof STORYBOARD_STATUSES)[number];

/**
 * What a presentation is made of. `images_and_video` (the default): frames,
 * clips and voiceovers. `images`: frames and voiceovers only - no clip is
 * shown, generated, adopted or added; clips it had before are kept, hidden,
 * and come back when it is switched back.
 */
export const STORYBOARD_MEDIA_MODES = ["images_and_video", "images"] as const;
export type StoryboardMediaMode = (typeof STORYBOARD_MEDIA_MODES)[number];
export const DEFAULT_STORYBOARD_MEDIA_MODE: StoryboardMediaMode = "images_and_video";

/**
 * The most frame options a shot holds. The portal offers no "Add frame" past
 * it, and the server refuses a new option past it (adopting into a new
 * option, creating a frame slot, restoring an archived one): the person
 * replaces one instead. The agent's publish is not capped by it.
 */
export const STORYBOARD_MAX_FRAMES_PER_SHOT = 5;

/**
 * The number a person calls a shot by, from its key: `s03` is 3, `S12` is 12.
 * Null for a key that is not `s` and digits; such a shot is called by its key.
 */
export function shotKeyNumber(key: string): number | null {
  const match = /^s0*(\d+)$/i.exec(key.trim());
  return match ? Number(match[1]) : null;
}

/**
 * Slot kinds the agent writes today. Not a closed list: a reader must tolerate
 * others. The v2 additions, all storyboard-level unless said otherwise:
 *  - `voiceover_take` - one continuous ElevenLabs take covering several lines
 *    (`meta.lines` names them), beside the stitched `voiceover` A and B;
 *  - `reference_frame` - a still lifted from the reference video for the deck;
 *  - `ref_image` - a reference image a generation was fed, keyed by the
 *    agent's library id (`tin-closed-topdown-studio`); `inputs[]` point at it;
 *  - `thumb` - the deck (16:9) and concept (9:16) thumbnails.
 */
export const STORYBOARD_SLOT_KINDS = [
  "frame",
  "clip",
  "voiceover",
  "voiceover_take",
  "reference_still",
  "reference_video",
  "reference_frame",
  "ref_image",
  "final_cut",
  "thumb",
  "image",
  "audio",
  "other",
] as const;
export type StoryboardSlotKind = (typeof STORYBOARD_SLOT_KINDS)[number] | (string & {});

export const STORYBOARD_TAKE_STATUSES = ["pending", "ready", "failed"] as const;
export type StoryboardTakeStatus = (typeof STORYBOARD_TAKE_STATUSES)[number];

/** Which door a change came through. */
export type StoryboardChangeSource = "agent_publish" | "agent_edit" | "portal";

/* ---------------------------------------------------------------- slot refs */

/** What a slot key may look like. Unchanged from v1. */
export const STORYBOARD_SLOT_KEY_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

/**
 * A slot named from outside its row: `s07/frame/v1` (a shot's slot),
 * `l03/voiceover/kate` (a line's), `ref_image/tin-closed-topdown-studio` (the
 * storyboard's). Used inside `inputs[]` and inside `meta.deck` wherever the
 * agent's deck config named a local file. Two or three segments, each a key.
 */
export const STORYBOARD_SLOT_REF_RE = /^(?:[A-Za-z0-9][A-Za-z0-9._-]{0,63}\/){1,2}[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

export type StoryboardSlotRef =
  /** A shot's or a line's slot, by the row's key. Which table the key is in, only the storyboard knows. */
  | { owner: "row"; key: string; kind: string; slot: string }
  | { owner: "storyboard"; key: null; kind: string; slot: string };

/**
 * Read a slot ref. Three segments name a shot's or a line's slot by the row's
 * key; two name a storyboard-level slot. The parser does not guess which table
 * a row key is in: the agent's naming (`s` for shots, `l` and `h` for lines)
 * is a convention, not a rule, so the resolver looks the key up among the
 * storyboard's shots and lines and refuses one that is in neither or in both.
 * Null for anything else, so a bad ref is one issue in a 400, never a crash.
 */
export function parseStoryboardSlotRef(ref: string): StoryboardSlotRef | null {
  if (!STORYBOARD_SLOT_REF_RE.test(ref)) return null;
  const parts = ref.split("/");
  if (parts.length === 2) return { owner: "storyboard", key: null, kind: parts[0]!, slot: parts[1]! };
  return { owner: "row", key: parts[0]!, kind: parts[1]!, slot: parts[2]! };
}

export function formatStoryboardSlotRef(ref: StoryboardSlotRef): string {
  return ref.owner === "storyboard" ? `${ref.kind}/${ref.slot}` : `${ref.key}/${ref.kind}/${ref.slot}`;
}

/* -------------------------------------------------------------- generation */

/** What a reference picture did in the request that made a frame or a clip. */
export const STORYBOARD_INPUT_ROLES = ["start_image", "end_image", "image_reference", "audio_reference"] as const;
export type StoryboardInputRole = (typeof STORYBOARD_INPUT_ROLES)[number];

/**
 * What kind of thing the input is, in the agent's library vocabulary; `audio` is a line's voiceover take
 * locked into a clip (`audio_reference`), the kind the platform's own Voiceover button records it as.
 */
export const STORYBOARD_INPUT_KINDS = [
  "identity",
  "scale",
  "pellet",
  "lifestyle",
  "character",
  "location",
  "style",
  "frame",
  "audio",
  "other",
] as const;
export type StoryboardInputKind = (typeof STORYBOARD_INPUT_KINDS)[number];

/**
 * One picture (or sound) a generation was fed, by slot. On a slot's `params`
 * it is the recipe for the next render ("use the current take of that slot");
 * on a take's `params` it is the record, with the exact bytes pinned by
 * `sha256`. `inputs[0]` of a clip is its start frame.
 */
export interface StoryboardInput {
  /** Position in the provider request, 1-based. */
  n: number;
  role: StoryboardInputRole;
  /** A slot ref, see `STORYBOARD_SLOT_REF_RE`. */
  slot: string;
  /** The exact bytes used. Always set on a take's params; on a slot's, absent means "the current take". */
  sha256?: string;
  kind?: StoryboardInputKind;
  /** The ASSET MAP sentence for this picture, as the prompt named it. */
  note?: string;
}

/**
 * The settings a frame slot renders with. `inputs` are the pictures it
 * attaches. Every field is optional on purpose: this is the wire shape, and a
 * take the agent could not trace to a finished job record arrives with `{}`
 * (and `generation.header: "missing"`), which a reader shows as "settings
 * unknown" rather than refuses.
 */
export interface StoryboardFrameParams {
  model?: string;
  resolution?: string;
  aspectRatio?: string;
  count?: number;
  inputs?: StoryboardInput[];
  [extra: string]: unknown;
}

/** The settings a clip slot renders with. Optional for the same reason as the frame's. */
export interface StoryboardClipParams {
  model?: string;
  mode?: string;
  /** Seconds, as the provider takes it. */
  duration?: number;
  resolution?: string;
  aspectRatio?: string;
  generateAudio?: boolean;
  count?: number;
  inputs?: StoryboardInput[];
  [extra: string]: unknown;
}

/** What a frame or clip slot's `meta` may carry beyond v1's status fields. */
export interface StoryboardVisualSlotMeta {
  status?: string;
  approved?: boolean;
  /** The variant, as the agent names it: `v1`, `v3`. */
  version?: string;
  state?: string;
  muted?: boolean;
  otherTakes?: unknown;
  pending?: boolean;
  sound?: string | null;
  /** The written prompt before assembly - what a person edits. */
  authored?: string;
  /** The per-picture job sentences, by input. */
  refJobs?: Record<string, string>;
  /** The variant's note: what this version changes. */
  note?: string;
  speaks?: boolean;
  audio?: string;
  [extra: string]: unknown;
}

/** Where a take came back from, and what the provider said about it. */
export interface StoryboardTakeGeneration {
  jobId?: string;
  route?: "hf-rest" | "hf-cli" | "gemini" | "elevenlabs" | (string & {});
  requestId?: string;
  template?: string;
  doneAt?: string;
  creditsEst?: number;
  paramsSha?: string;
  promptSha?: string;
  /** The provider served a lesser model or resolution than asked: what was asked, what came back, and why. */
  downgraded?: { from?: string; to?: string; why?: string };
  qa?: unknown;
  /**
   * `missing` marks a take the agent could not trace to a finished job
   * record (an older project, a cleaned queue): its bytes are real, but its
   * prompt is sent empty and its params as `{}`, and a reader says so rather
   * than showing the slot's current prompt as if it had made this take.
   */
  header?: "missing" | (string & {});
  [extra: string]: unknown;
}

/* -------------------------------------------------------------- voiceovers */

export const VOICEOVER_PROVIDERS = ["elevenlabs", "in_clip", "upload"] as const;
export type VoiceoverProvider = (typeof VOICEOVER_PROVIDERS)[number];

/**
 * ElevenLabs' knobs, as the agent's `media vo` sends them. All optional: a
 * provider's defaults are not ours to spell. `style` and `speed` are v3-era
 * and never sent on v4 (older takes still carry them).
 */
export interface VoiceSettings {
  stability?: number;
  similarity?: number;
  style?: number;
  speed?: number;
  seed?: number;
  language?: string;
  /** A rate multiplier, as `media vo` records it: 1 is the voice's own pace. */
  tempo?: number;
  [extra: string]: unknown;
}

/**
 * How a voiceover was (or will be) made. On the slot: the recipe; on the
 * take: the snapshot. `in_clip` means the words are spoken inside the shot's
 * clip and there is no separate audio; `upload` means a person supplied the
 * file. The same shape sits on the storyboard-level whole-ad reads
 * (`voiceover/A`, `/B`), without `text`: the script they read is the lines.
 */
export interface VoiceoverParams {
  provider: VoiceoverProvider;
  voiceId?: string;
  voiceName?: string;
  /** Our alias for the voice: `kate`, `uk-male`, `scandi-narrator`. Also the slot key. */
  voiceAlias?: string;
  /** `eleven_v4` (older agent takes: `eleven_v3`). */
  model?: string;
  settings?: VoiceSettings;
  /**
   * The exact text sent, `[audio tags]` included, so a regeneration starts
   * from what actually went out. Required on a line's `elevenlabs` slot (the
   * server holds it to that); for `in_clip` and `upload` nothing was sent and
   * the line's own text is the words, so it is optional there.
   */
  text?: string;
  tags?: string[];
  [extra: string]: unknown;
}

/** What a line's voiceover slot keeps in `meta`. */
export interface StoryboardVoiceoverSlotMeta {
  /** The voice the editors should use when a line has several. */
  primary?: true;
  /** Which continuous take (`voiceover_take` slot) this line was cut from, and where. */
  take?: number;
  takeFile?: string;
  cut?: { startMs: number; endMs: number };
  character?: string;
  /** For `in_clip`: the shot whose clip carries the speech. */
  sourceClip?: string;
  qa?: { verdict: string; flags: string[] };
  [extra: string]: unknown;
}

/** `meta` on the storyboard-level `voiceover/A` and `/B`: how the full read was made. */
export interface StoryboardWholeVoiceoverMeta {
  /** `stitched`: the line takes joined end to end, in step with the shots; `onetake`: the whole script in one generation. */
  kind: "stitched" | "onetake";
  /** A sentence for the editor on what that means for the cut. */
  how?: string;
  [extra: string]: unknown;
}

/** `meta` on a `voiceover_take` slot: one continuous ElevenLabs take and the lines it covers. */
export interface StoryboardVoiceoverTakeMeta {
  n: number;
  /** Line keys, in the order they were read. */
  lines: string[];
  [extra: string]: unknown;
}

/** One voice in the storyboard's cast, as `meta.voices.cast[]` lists it. */
export interface StoryboardVoice {
  alias: string;
  voiceId: string;
  name: string;
  provider: "elevenlabs" | (string & {});
}

/* ------------------------------------------------- storyboard-level slots */

/**
 * `meta` on a `ref_image` slot: what the agent's library says about the
 * picture, so a strategist editing a shot starts from the same inputs the
 * agent used. `mediaId` is set only when it is a Higgsfield uuid, never a
 * `local-*` id; `missing: true` marks a picture named by a job but absent
 * from the publishing machine's libraries, sent without its bytes and with
 * `library: "unknown"`.
 */
export interface StoryboardRefImageSlotMeta {
  roles: string[];
  labelText?: "premium" | "whitening";
  notes?: string;
  library: "brand" | "style" | "unknown";
  /** True for a standard picture every run attaches. */
  sop: boolean;
  mediaId?: string;
  missing?: boolean;
  [extra: string]: unknown;
}

/** `meta` on a `reference_frame` slot: a still lifted from the reference video for the deck. */
export interface StoryboardReferenceFrameMeta {
  /** Seconds into the reference video. */
  at?: number;
  [extra: string]: unknown;
}

/** `meta` on the `reference_video/ref` slot, as v1. */
export interface StoryboardReferenceVideoMeta {
  url?: string;
  label?: string;
  [extra: string]: unknown;
}

/* ------------------------------------------------------------- derivatives */

/**
 * A derivative the agent cuts beside a take's master, because the API image
 * has no ffmpeg: `thumb` is a 540-wide JPEG of a frame or still, `poster` a
 * JPEG of a clip's first frame, `web` a 720p H.264 mp4 of a clip (480p for the
 * reference video). The platform serves each at `.../takes/:takeId/<kind>` and
 * falls back to the master when a kind is missing.
 */
export const STORYBOARD_TAKE_FILE_KINDS = ["thumb", "poster", "web"] as const;
export type StoryboardTakeFileKind = (typeof STORYBOARD_TAKE_FILE_KINDS)[number];

/** A derivative as a publish declares it. Uploaded through the same handshake as the take's own bytes. */
export interface StoryboardTakeFileInput {
  kind: StoryboardTakeFileKind;
  sha256: string;
  bytes: number;
  contentType: string;
  width?: number | null;
  height?: number | null;
  durationMs?: number | null;
}

/** A take's derivatives as a reader gets them: a URL per kind that has arrived. */
export type StoryboardTakeFiles = Partial<Record<StoryboardTakeFileKind, string>>;

/* ------------------------------------------------------------------- lines */

/** How a line's words reach the viewer. `dialogue` is speech inside the clip itself. */
export const STORYBOARD_LINE_DELIVERIES = ["voiceover", "dialogue", "on_screen", "none"] as const;
export type StoryboardLineDelivery = (typeof STORYBOARD_LINE_DELIVERIES)[number];

/** One on-screen overlay the brief asks for while a line plays: a caption, a card, a label. */
export interface StoryboardOnScreenOverlay {
  kind?: string;
  items?: string[];
  /** True when the items appear one after another rather than together. */
  sequence?: boolean;
  [extra: string]: unknown;
}

/** What a line's `meta` may carry. */
export interface StoryboardLineMeta {
  hook?: unknown;
  /** The overlays over this line, in order; a v1 body sent one string. */
  onScreen?: StoryboardOnScreenOverlay[] | string | null;
  voSeconds?: number;
  /** The beat table's columns: why this line is here, and what is on screen. */
  script?: { why?: string; onScreen?: string };
  /** Which of the line's voiceover slots the editors should use. */
  voiceSlotKey?: string;
  /**
   * The server's mark, never sent: true when the line's `durationMs` is its
   * own length (set by a person or the agent while it had no shots) rather
   * than the sum its shots made. A sent value is ignored. `computeTimeline`
   * reads it (`LINE_DURATION_SET`).
   */
  durationSet?: boolean;
  [extra: string]: unknown;
}

/** The strategist's note on a line: one editable text, shown yellow to the editor. Portal-only; a publish never carries it. */
export interface StoryboardLineNote {
  bodyMd: string;
  updatedAt: string;
  updatedBy: { id: number; name: string } | null;
}

/* -------------------------------------------------------------------- meta */

/** What `storyboards.meta` may carry in v2, beyond v1's logline, intro, caveats, handover, style and hooks. */
export interface StoryboardMeta {
  logline?: string;
  intro?: string;
  editor?: string;
  caveats?: string[];
  handover?: { format?: string[]; captions?: string[]; sound?: string[]; text?: string[] };
  reference?: {
    label?: string;
    url?: string;
    note?: string;
    heading?: string;
    /** The reference read, one titled paragraph each (Story, Characters, Look...); a v1 body sent plain strings. */
    read?: Array<{ title: string; body: string }> | string[];
    stats?: Record<string, unknown>;
    source?: { platform: "meta" | "tiktok" | "other"; adId?: string };
    [extra: string]: unknown;
  };
  style?: unknown;
  hooks?: Array<{ n: number; line: string; title?: string; note?: string }>;
  /** The agent's `deck.json` verbatim, every local path replaced by a slot ref. */
  deck?: Record<string, unknown>;
  voices?: {
    cast: StoryboardVoice[];
    /** The run's defaults, when one run read every line. */
    voiceover?: { name?: string; model?: string; settings?: VoiceSettings };
  };
  [extra: string]: unknown;
}

/** What a shot's `meta` may carry beyond v1. */
export interface StoryboardShotMeta {
  /** The brief's span in reference time, kept for the record. Never the ad's clock: `cutToMs` and the timeline are. */
  plan?: { startMs?: number; endMs?: number };
  [extra: string]: unknown;
}

/** The reference shot a shot adapts: description for whoever writes the prompt, never a prompt itself. */
export interface StoryboardShotReference {
  /** Seconds into the reference video where that shot runs. */
  from?: number | null;
  to?: number | null;
  at?: number | null;
  starts?: string | null;
  ends?: string | null;
  camera?: string | null;
  inside?: string | null;
  onScreen?: string | null;
  text?: string | null;
  /** The ad a borrowed still comes from, when it is not the reference being rebuilt. */
  sourceAd?: string | null;
  [extra: string]: unknown;
}

export interface StoryboardTake {
  id: number;
  n: number;
  status: StoryboardTakeStatus;
  /**
   * Where to fetch the bytes. From the agent API: a signed URL good for an
   * hour. From the portal: a session-checked path that redirects to one. Null
   * while the take is pending or failed.
   */
  url: string | null;
  sha256: string | null;
  contentType: string | null;
  bytes: number | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  prompt: string | null;
  params: Record<string, unknown>;
  model: string | null;
  provider: string | null;
  generation: Record<string, unknown>;
  source: string;
  createdAt: string;
  storedAt: string | null;
  /**
   * The derivatives that have arrived, as URLs by kind. Empty when the take
   * has none (or the read asked for no media), in which case the master at
   * `url` is what to show.
   */
  files: StoryboardTakeFiles;
  /**
   * The take's still as signed Cloudflare copies, when they are ready and the
   * CDN is on (portal reads only): draw from these and skip the origin's
   * redirect. Small copies come from the thumb or poster, larger ones from an
   * image master. Absent means "use `files` and `url` as before"; when a copy
   * fails to load, fall back to those.
   */
  cdn?: CdnStill | null;
}

/**
 * A still's signed Cloudflare copies, inside a list or a document answer, so
 * the browser draws the picture straight from Cloudflare instead of asking our
 * origin for the redirect first. The URLs are the same all UTC day and stop
 * working at `expiresAt`; a cached answer older than that must not use them.
 */
export interface CdnStill {
  /** The smallest copy (240 wide, or the picture's own width when smaller). */
  src: string;
  /** Every copy up to the picture's own width: `"<url> 240w, <url> 480w, ..."`. */
  srcSet: string;
  /** The widest copy's width, when known. */
  width: number | null;
  /** Seconds since the epoch. */
  expiresAt: number;
}

export interface StoryboardSlot {
  id: number;
  kind: StoryboardSlotKind;
  key: string;
  label: string | null;
  position: number;
  /** The prompt as it stands now; edit it, re-render, and the result is a new take. */
  prompt: string | null;
  params: Record<string, unknown>;
  meta: Record<string, unknown>;
  /** The take on show, or null when none has arrived. */
  current: StoryboardTake | null;
  /** A take declared by the last publish whose bytes have not arrived yet. */
  pendingTakeId: number | null;
  /**
   * The clip editor's in/out on a take of this slot (a shot's clip, a line's
   * voiceover), absent when it has none. It applies only while the take it
   * names is `current` (`activeTrim` in `clip-editor.ts`).
   */
  trim?: StoryboardSlotTrim | null;
  takeCount: number;
  /** Every take, oldest first - only when the read asked for history. */
  takes?: StoryboardTake[];
}

export interface StoryboardShot {
  id: number;
  key: string;
  position: number;
  /** The line it plays under, or null for a shot that belongs to no line. */
  line: string | null;
  description: string | null;
  action: string | null;
  onScreenText: string | null;
  cutToMs: number | null;
  durationMs: number | null;
  startMs: number | null;
  cuts: number;
  reference: StoryboardShotReference | null;
  meta: Record<string, unknown>;
  slots: StoryboardSlot[];
  /**
   * Which video track the shot is on (migration 0045, docs/clip-editor/03-overlay-track.md):
   * `main` (Video 1, laid end to end and timing its line - every shot before
   * overlays existed) or `overlay` (Video 2: a B-roll shown `overlay.offsetMs`
   * into its line, above whatever plays underneath, timing nothing).
   * Absent on a snapshot written before it existed; read it as `main`.
   */
  layer?: StoryboardShotLayer;
  /** Where an overlay sits and how it sounds; null on a Video 1 shot. */
  overlay?: StoryboardShotOverlay | null;
}

export type StoryboardShotLayer = "main" | "overlay";

/**
 * An overlay's place and sound. It shows `offsetMs` after its own line
 * starts, in every cut that line is in (a hook's overlay in that hook's cut
 * only), so it moves with its line when shots before it are trimmed; it may
 * run past the line's end. Its length is the shot's `cutToMs`, its picture
 * the shot's clip with its trim.
 */
export interface StoryboardShotOverlay {
  offsetMs: number;
  /** "B-roll" when null. */
  label: string | null;
  /** Default true: the clip underneath keeps speaking. */
  muted: boolean;
  /** Linear gain on its sound when unmuted, 0-2 (1 = as recorded). */
  gain: number;
}

export interface StoryboardLine {
  id: number;
  key: string;
  position: number;
  kind: string;
  variantOf: string | null;
  speaker: string | null;
  delivery: string | null;
  /** The script part. */
  text: string;
  startMs: number | null;
  durationMs: number | null;
  meta: Record<string, unknown>;
  /** The line's own media - its voiceover take, or one slot per voice alternative. */
  slots: StoryboardSlot[];
  /** The shots it cuts to, in order. */
  shots: StoryboardShot[];
  /**
   * The strategist's note for this line, or null when there is none. Saved
   * like every other field: the editors read the note the snapshot holds,
   * so a note reaches them when it is saved.
   */
  note: StoryboardLineNote | null;
}

export interface StoryboardDocument {
  id: number;
  slug: string;
  title: string;
  brand: string | null;
  format: string | null;
  schemaVersion: number;
  language: string | null;
  aspect: string | null;
  runtimeMs: number | null;
  /** `images` leaves every clip out of this document; see `STORYBOARD_MEDIA_MODES`. */
  mediaMode: StoryboardMediaMode;
  /** Live clip slots with a take, kept but left out because the storyboard is images-only; 0 otherwise. Switching back shows them. */
  hiddenClips: number;
  status: StoryboardStatus;
  /** Send it back as `baseRevision` on a write to be refused rather than overwrite a newer change. */
  revision: number;
  meta: Record<string, unknown>;
  brief: { id: number; ref: string; title: string } | null;
  presentation: { id: number; slug: string; url: string } | null;
  /** Storyboard-level media: the reference video, both voiceovers, the final cut. */
  media: StoryboardSlot[];
  lines: StoryboardLine[];
  /** Shots that belong to no line, in order. */
  looseShots: StoryboardShot[];
  /**
   * Free voiceover clips: voiceovers the clip editor placed anywhere on the
   * timeline, owned by no line (`StoryboardVoiceClip`), by key. Absent on a
   * snapshot written before they existed; read it as empty.
   */
  voiceClips?: StoryboardVoiceClip[];
  pendingTakes: number;
  sourceLabel: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  /**
   * The published state. On a live read: the editors' deck renders the
   * snapshot taken at `publishedRevision`, and `unpublishedChanges` is how
   * many revisions the live document is ahead of it, which is what the
   * "Publish changes (n)" button shows - null and 0 before the first publish.
   * On a snapshot read, the snapshot's own revision and time, and 0.
   */
  publishedRevision: number | null;
  publishedAt: string | null;
  unpublishedChanges: number;
  /** Derived from durations by `computeTimeline`; never edited directly. */
  timeline: StoryboardTimeline;
  /**
   * What the person reading may do here, attached by the portal's reads
   * (`GET /:id`, `GET /:id/view`); never stored in a snapshot, absent on the
   * agent API. See `StoryboardRights`.
   */
  can?: StoryboardRights;
  /**
   * True on a lean portal read (`?lean=1`): every slot's and take's prompt,
   * and the slot's `meta.authored` and `meta.refJobs`, were left out - about
   * half the document's bytes. `GET /:id/prompts` answers them
   * (`StoryboardPromptsResponse`) for the places that show or reuse a prompt.
   * Absent on a full read and on the agent API.
   */
  promptsOmitted?: true;
}

/**
 * A free voiceover clip (clip editor; STORYBOARDS.md, "Free voiceovers"): a
 * voiceover placed anywhere on the timeline, owned by no line. Its `slot` (kind
 * `voiceover`, key = the clip's key) holds its takes, its current take and its
 * trim exactly as a line's voiceover slot does.
 *
 * Where it plays: `hook` names the opening it belongs to (the opening line's
 * key: `l01` for hook 1, `h2`...), and `startMs` is then from the cut's start;
 * it plays only in that opening's cut. `hook: null` puts it on the body:
 * `startMs` is from the end of the opening, so it plays in every opening's cut
 * and moves with the body when the hook is switched (`voiceClipStartMs`).
 */
export interface StoryboardVoiceClip {
  id: number;
  /** `x1`, `x2`... minted by the server, never reused. */
  key: string;
  hook: string | null;
  startMs: number;
  label: string | null;
  meta: Record<string, unknown>;
  slot: StoryboardSlot;
}

/** The prompts a lean read left out, by slot id and take id. */
export interface StoryboardPromptsResponse {
  slots: Record<string, { prompt: string | null; authored?: unknown; refJobs?: unknown }>;
  takes: Record<string, string | null>;
}

/**
 * The concept stages in which its assigned editor may generate and pick on
 * its storyboard (round 3, D1). Outside them the editor reads the deck only.
 */
export const EDITOR_WORK_STAGES = ["assigned", "in_progress", "changes_requested"] as const;

/**
 * Who may do what on one storyboard, for the person reading it. The server
 * enforces every one of these; the web only switches views on them.
 *
 * - admin, strategist: everything true.
 * - the concept's assigned editor, while it is in `EDITOR_WORK_STAGES`:
 *   `generate`, `pick`, `save`, `live`, `gallery`, `download` true;
 *   `editScript` and `manage` false.
 * - any other editor who may see it (released, theirs): `download` only (the
 *   deck's selected media); `reason` says why the rest is off.
 */
export interface StoryboardRights {
  role: "admin" | "strategist" | "editor" | null;
  /** Generate frames, clips and line voiceovers (strategists also whole reads and stitches), and upload references. */
  generate: boolean;
  /** Choose the main frame, add or remove frame options, choose the clip and a line's voiceover take. */
  pick: boolean;
  /** Script text, lines and shots structure, prompts on slots, meta, notes, presentation type: strategists and admins only. */
  editScript: boolean;
  /** The save bar: save state, Save (which publishes), Discard, and undo of one's own steps. */
  save: boolean;
  /** Read the live document (an editor asks with `GET /:id?live=true`); without it, the published snapshot. */
  live: boolean;
  /** Galleries, the generations read and unadopted output media. */
  gallery: boolean;
  /** Download selected media (`GET /:id/slots/:slotId/download`). */
  download: boolean;
  /** Strategist-only generation tools: hides, Voiceover A and B, uploads as takes, voices and library edits, publish. */
  manage: boolean;
  /** Why an editor may not work here now: `stage` (not in EDITOR_WORK_STAGES), `not_assigned`, `archived`, `no_account`; null when they may. */
  reason: "stage" | "not_assigned" | "archived" | "no_account" | null;
}

/** The 403 codes an editor meets (round 3, D1). */
export const EDITOR_REFUSAL_CODES = {
  /** Never an editor's: the script, lines, shots structure, prompts, meta, notes, presentation type, publish. */
  scriptLocked: "script_locked",
  /** Strategist-only generation tools: hides, Voiceover A/B, take uploads, voices and library edits, someone else's run. */
  strategistOnly: "strategist_only",
  /** The editor's kind of work, but not now: the concept is not theirs or not in an editable stage. */
  editorLocked: "editor_locked",
} as const;

/** What the deck view draws: the published document, or the live one for a strategist looking before the first publish. */
/**
 * A concept's storyboard read by the concept's ref, in one round trip:
 * `GET /api/pipeline/briefs/:ref/storyboard` answers what
 * `GET /api/pipeline/storyboards/:id` answers for the same flags (`live`,
 * `published`, `history`, `archived`, `lean`), plus the id. With `?view=1`
 * it answers the deck's `StoryboardViewResponse` instead, plus the id.
 * `.../storyboard/prompts` and `.../editor` are the same for the prompts and
 * the clip editor's session. A brief with no storyboard is a 404
 * `no_storyboard`; one the reader may not see, `brief_not_found`.
 */
export interface BriefStoryboardResponse {
  storyboardId: number;
  storyboard: StoryboardDocument;
}

export type BriefStoryboardViewResponse = StoryboardViewResponse & { storyboardId: number };

/** `GET /api/pipeline/briefs/:ref/storyboard/prompts` (same flags as the document read, or `?view=1`). */
export type BriefStoryboardPromptsResponse = StoryboardPromptsResponse & { storyboardId: number };

export interface StoryboardViewResponse {
  storyboard: StoryboardDocument;
  /** False only for an admin or a strategist reading a storyboard nobody has published yet. */
  published: boolean;
  publishedRevision: number | null;
  publishedAt: string | null;
}

/** One stored snapshot, as a history list names it. */
export interface StoryboardSnapshotSummary {
  id: number;
  revision: number;
  /** `publish` (the button) | `release` (brief released to editors) | `revision` (sent back) */
  reason: string;
  createdBy: { id: number; name: string } | null;
  createdAt: string;
}

export interface StoryboardSummary {
  id: number;
  slug: string;
  title: string;
  brand: string | null;
  format: string | null;
  mediaMode: StoryboardMediaMode;
  status: StoryboardStatus;
  revision: number;
  /** The revision the editors' deck renders; null until the first publish. */
  publishedRevision: number | null;
  brief: { id: number; ref: string; title: string } | null;
  presentationId: number | null;
  /** The hosted deck rendered from it, by slug - what an agent holding a deck slug looks for. */
  presentation: { id: number; slug: string } | null;
  lineCount: number;
  shotCount: number;
  pendingTakes: number;
  updatedAt: string;
}

/* ----------------------------------------------------------------- comments */

export type StoryboardCommentTarget =
  | { kind: "storyboard" }
  | { kind: "line"; key: string; id: number }
  | { kind: "shot"; key: string; id: number };

export interface StoryboardReaction {
  emoji: string;
  count: number;
  /** True when the reader is one of them. */
  mine: boolean;
  names: string[];
}

export interface StoryboardComment {
  id: number;
  target: StoryboardCommentTarget;
  parentId: number | null;
  author: { userId: number | null; name: string; role: string | null; viaAgent: boolean };
  bodyMd: string;
  mentions: number[];
  pinned: boolean;
  resolvedAt: string | null;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  editCount: number;
  reactions: StoryboardReaction[];
  /** Replies, oldest first. Only top-level comments carry them. */
  replies: StoryboardComment[];
}

export interface StoryboardCommentEdit {
  previousBodyMd: string;
  editedByUserId: number | null;
  editedAt: string;
}

/* ------------------------------------------------------------------ history */

export interface StoryboardRevision {
  id: number;
  storyboardRevision: number;
  entity: "storyboard" | "line" | "shot" | "slot" | "take" | (string & {});
  entityId: number;
  entityKey: string | null;
  action: string;
  /** `{ field: { from, to } }` for an update; the new row for a create. */
  changes: Record<string, unknown>;
  source: StoryboardChangeSource | (string & {});
  actor: { userId: number | null; name: string | null; viaAgent: boolean };
  note: string | null;
  at: string;
}

/* ------------------------------------------------------------- the writes */

/**
 * One take as a publish declares it: the bytes by hash, and what made them.
 *
 * In v2 `params` is the slot's params with every input's `sha256` resolved
 * (an immutable snapshot), `generation` is typed, and `files` declares the
 * derivatives uploaded beside the master. Header data is attached only when
 * the agent's job record is done and the file's bytes match it, so an
 * in-flight re-roll never mislabels the previous file.
 */
export interface StoryboardTakeInput {
  sha256: string;
  bytes: number;
  contentType: string;
  width?: number | null;
  height?: number | null;
  durationMs?: number | null;
  prompt?: string | null;
  params?: Record<string, unknown>;
  model?: string | null;
  provider?: string | null;
  generation?: StoryboardTakeGeneration;
  files?: StoryboardTakeFileInput[];
}

/**
 * A slot as a publish declares it. `params` is open on the wire; for a frame
 * or clip slot it is `StoryboardFrameParams` / `StoryboardClipParams` (with
 * `inputs[]`), for a voiceover slot `VoiceoverParams`, and the server checks
 * the shape when `schemaVersion` is 2.
 */
export interface StoryboardSlotInput {
  /** The owner: a shot key, a line key, or neither for storyboard-level media. Never both. */
  shot?: string | null;
  line?: string | null;
  kind: string;
  key: string;
  label?: string | null;
  prompt?: string | null;
  params?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  /** The take that should be on show. Omitted: the slot's takes are left as they are. */
  take?: StoryboardTakeInput | null;
}

/**
 * A line as a publish declares it. Timing: `durationMs` only for a line
 * without shots (its voiceover's length); `startMs` may be sent but the server
 * derives it, and a hook variant is sent without one. `noteMd` is never here:
 * the note is the portal's, and a publish never touches it.
 */
export interface StoryboardLineInput {
  key: string;
  kind?: string;
  variantOf?: string | null;
  speaker?: string | null;
  delivery?: StoryboardLineDelivery | (string & {}) | null;
  text: string;
  startMs?: number | null;
  durationMs?: number | null;
  meta?: Record<string, unknown>;
}

/**
 * A shot as a publish declares it. `cutToMs` is the edit length (from the
 * real cut when there is one), `durationMs` the generated clip's length;
 * `startMs` is derived by the server and ignored when sent.
 */
export interface StoryboardShotInput {
  key: string;
  line?: string | null;
  description?: string | null;
  action?: string | null;
  onScreenText?: string | null;
  cutToMs?: number | null;
  durationMs?: number | null;
  startMs?: number | null;
  cuts?: number;
  reference?: StoryboardShotReference | null;
  meta?: Record<string, unknown>;
  /**
   * `overlay` publishes the shot on Video 2: a B-roll over a clip that keeps
   * running underneath (docs/clip-editor/03-overlay-track.md). Its `cutToMs`
   * is how long it shows; it adds nothing to its line's length. Default `main`.
   */
  layer?: StoryboardShotLayer;
  /**
   * For an overlay: the Video 1 shot it sits over (`s06`); it starts
   * `overOffsetMs` after that shot starts. Without it, `overOffsetMs` is from
   * the start of the overlay's own line.
   */
  overOf?: string | null;
  overOffsetMs?: number | null;
  /** For an overlay: its name ("B-roll" when left out), and its sound - muted unless `muted: false`, at `gain` (0-2, default 1). */
  overlay?: { label?: string | null; muted?: boolean; gain?: number } | null;
}

export interface StoryboardPublishInput {
  /** An existing storyboard's slug to re-publish; omitted, a new one is created. */
  slug?: string;
  title: string;
  brand?: string | null;
  format?: string | null;
  /** 1 (what the first agents sent) or 2 (the contract above). Missing means 1. */
  schemaVersion?: 1 | 2;
  language?: string | null;
  aspect?: string | null;
  runtimeMs?: number | null;
  /** `images` for an images-only presentation; omitted on create means `images_and_video`, on a re-publish leaves it as it is. */
  mediaMode?: StoryboardMediaMode;
  meta?: Record<string, unknown>;
  sourceLabel?: string | null;
  /** Link to a concept (`CPT-3001`) and to the hosted deck rendered from this storyboard. `null` unlinks. */
  briefRef?: string | null;
  presentationSlug?: string | null;
  /**
   * What the ad sells, set on the concept this storyboard briefs (`briefRef`,
   * or the one it is already linked to). Left out, the concept keeps its own
   * product, which is `gum` unless somebody chose otherwise.
   */
  product?: ConceptProduct;
  /**
   * What kind of concept it is (a key from the category list, or its label),
   * set on the linked concept like `product`. Left out, the concept keeps its
   * own; with no concept linked yet, nothing is written and the result says so.
   */
  category?: string;
  /**
   * `replace` (default): the storyboard becomes exactly what was sent - lines,
   * shots and slots it no longer names are archived (never deleted: their
   * comments stay). `merge`: only what was sent is created or updated, and
   * what a person did in the portal is kept - a take they picked stays
   * picked, a line or shot they created is never archived. Once the portal
   * has edited a storyboard, the server refuses a `replace` that would undo a
   * portal-created row unless `force` is set.
   */
  mode?: "replace" | "merge";
  /** Override the refusal above. Never overrides a line's note, which no publish writes. */
  force?: boolean;
  /** The revision this publish was built from; a newer one on the server is a 409. */
  baseRevision?: number;
  transport?: "signed" | "proxy";
  lines?: StoryboardLineInput[];
  shots?: StoryboardShotInput[];
  slots?: StoryboardSlotInput[];
}

export interface StoryboardUploadTarget {
  sha256: string;
  bytes: number;
  contentType: string;
  /**
   * Where to PUT the bytes. `transport` says which route this is - `proxy`
   * is our blob route and wants the key, `signed` is the bucket's URL and
   * must not carry it - so the uploader never has to read it off the URL.
   * Absent from a server that does not say it yet.
   */
  upload: { method: "PUT"; url: string; headers: Record<string, string>; expiresAt?: string; transport?: "signed" | "proxy" };
}

export interface StoryboardPublishResult {
  storyboardId: number;
  slug: string;
  created: boolean;
  revision: number;
  status: StoryboardStatus;
  changed: { created: number; updated: number; archived: number; restored: number; takes: number };
  /** Bytes the bucket does not hold yet - takes and derivatives alike. Upload each, then finalize. */
  missing: StoryboardUploadTarget[];
  uploadUrlsExpireAt: string | null;
  next: string;
  /**
   * What the server kept against the body, because a person did it in the
   * portal and the publish did not say `force`: `slots` (`s07/frame/v1`) whose
   * new take was added but not put on show, because the person picked a take;
   * `fields` (`l03.text`, `s07/frame/v1.prompt`, `l05.archivedAt`) the body
   * named but the person had edited, or rows they had archived; the
   * storyboard's own as `storyboard.title`, `storyboard.briefRef`,
   * `storyboard.meta.logline`. Absent from a server that does not report it yet.
   */
  held?: { slots: string[]; fields?: string[] };
  /**
   * What a publish with `force: true` went over: the same names `held` would
   * have carried without it, and `rows` - the rows a person had a hand on that
   * the forced replace archived. Empty without `force`. Absent from a server
   * that does not report it yet.
   */
  overridden?: { slots: string[]; fields: string[]; rows: string[] };
  /**
   * True once a person has changed this storyboard in the portal (a revision
   * with `source: "portal"` exists). The agent's next publish should be a
   * `merge`; a `replace` without `force` is refused when it would undo a row
   * they made. Absent from a server that does not report it yet.
   */
  portalEdited?: boolean;
  /**
   * The category the publish named, and whether it was set on a concept
   * (`applied` false: the storyboard briefs no concept yet). Absent when the
   * publish named none.
   */
  category?: { key: string; label: string; applied: boolean };
}

export interface StoryboardFinalizeResult {
  storyboardId: number;
  slug: string;
  status: StoryboardStatus;
  revision: number;
  stored: number;
  /** Takes whose bytes are still not in the bucket. */
  missing: Array<{ sha256: string; slots: string[] }>;
  /** Takes whose stored size disagrees with what was declared. They stay pending. */
  mismatched: Array<{ sha256: string; declaredBytes: number; storedBytes: number }>;
}

/** A field edit from a person or an agent. Only the named fields change. */
export interface StoryboardLinePatch {
  text?: string;
  speaker?: string | null;
  delivery?: string | null;
  kind?: string;
  variantOf?: string | null;
  startMs?: number | null;
  durationMs?: number | null;
  meta?: Record<string, unknown>;
  /** The strategist's note; `null` clears it. Portal only, strategist or admin; the agent API refuses it. */
  noteMd?: string | null;
}

export interface StoryboardShotPatch {
  line?: string | null;
  description?: string | null;
  action?: string | null;
  onScreenText?: string | null;
  cutToMs?: number | null;
  durationMs?: number | null;
  startMs?: number | null;
  cuts?: number;
  reference?: StoryboardShotReference | null;
  meta?: Record<string, unknown>;
}

export interface StoryboardSlotPatch {
  label?: string | null;
  prompt?: string | null;
  params?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  /** Put an earlier take back on show. Must be a ready take of this slot. */
  currentTakeId?: number;
}

/**
 * Switching a storyboard's media mode (`PATCH /storyboards/:id`, both doors).
 * Switching to `images` while live clips hold a take is refused as
 * `409 clips_present` with `clips` (`s03/clip/clip`) unless `hideClips: true`
 * says the person saw them: the clips are then kept, hidden, and come back
 * when it is switched to `images_and_video` again.
 */
export interface StoryboardMediaModePatch {
  mediaMode?: StoryboardMediaMode;
  hideClips?: boolean;
}

/**
 * What the ad sells, set from the presentation editor (`PATCH /storyboards/:id`).
 * The value lives on the concept the storyboard briefs (`pipeline_briefs.product`,
 * the one source the creatives sheet reads); the change is a storyboard
 * revision all the same, so the save bar counts it and Discard or an undo puts
 * the concept's product back. Refused as `409 conflict` on a storyboard that
 * briefs no concept.
 */
export interface StoryboardProductPatch {
  product?: ConceptProduct;
}

export interface StoryboardPatchMeta {
  baseRevision?: number;
  /** Why, in a sentence, kept on the revision row. */
  note?: string | null;
}

/** What every edit route answers. An edit that changed nothing is not a revision, so `changed` is false and `revision` is unmoved. */
export interface StoryboardEditResult {
  revision: number;
  changed: boolean;
}

/* ------------------------------------------------------- structural edits */

/*
 * Adding, removing and reordering lines, shots and slots from the portal (and
 * the agent). Every one runs under the storyboard's lock with `baseRevision`,
 * answers `StoryboardEditResult`, records a revision row with its source, and
 * is followed by a timeline recompute. Keys are minted by the server, never
 * sent: `l` + zero-padded max+1 for lines (`h` + max+1 for hooks), `s` + max+1
 * for shots, counted over every row the storyboard has had, so a new row never
 * lands on an existing one. The agent's next publish may name the same key
 * (its next script line gets the same number); the publish then keeps every
 * field the person wrote and names it in `held`.
 */

/** POST /:id/lines. A new line starts with no shots and no voiceover. */
export interface StoryboardLineCreateInput {
  /** The line to insert after; omitted, the line goes last. */
  after?: string;
  /** `line` (the default) or `hook`; the server mints the key by it, so nothing else is accepted. */
  kind?: "line" | "hook";
  /** For a hook: the line it stands in for. */
  variantOf?: string | null;
  text: string;
  speaker?: string | null;
  delivery?: StoryboardLineDelivery | null;
  /** Only meaningful while the line has no shots; once it has, the shots decide. */
  durationMs?: number | null;
}

/** What POST /:id/lines answers: the key the server minted. */
export interface StoryboardLineCreateResult extends StoryboardEditResult {
  key: string;
}

/** DELETE /:id/lines/:key. Archives the line and, by default, its shots and their slots. */
export interface StoryboardLineDeleteInput {
  /** `detach` leaves the shots in place with no line. */
  shots?: "archive" | "detach";
}

/** POST /:id/lines/order. Must name every live line, each once. */
export interface StoryboardLineOrderInput {
  keys: string[];
}

/** POST /:id/shots. A new shot starts with no slots. */
export interface StoryboardShotCreateInput {
  line: string;
  /** The shot (of that line) to insert after; omitted, the shot goes last in its line. */
  after?: string;
  description?: string | null;
  action?: string | null;
  cutToMs?: number | null;
}

/** What POST /:id/shots answers: the key the server minted. */
export interface StoryboardShotCreateResult extends StoryboardEditResult {
  key: string;
}

/** POST /:id/shots/order. Reorders within one line; moving between lines is `PATCH /shots/:key { line }`. */
export interface StoryboardShotOrderInput {
  line: string;
  keys: string[];
}

/** POST /:id/slots. A new frame option, a second voice; the owner is a shot, a line, or neither. */
export interface StoryboardSlotCreateInput {
  shot?: string;
  line?: string;
  kind: string;
  /** Omitted, the server picks the next free key of that kind (`v6`). */
  key?: string;
  label?: string | null;
  prompt?: string | null;
  params?: Record<string, unknown>;
}

/** What POST /:id/slots answers: the slot's id and its key, minted when none was sent. */
export interface StoryboardSlotCreateResult extends StoryboardEditResult {
  id: number;
  key: string;
}

/** POST /:id/restore. Undo an archive. Lines and shots by key, slots by id. */
export type StoryboardRestoreInput =
  | { entity: "line"; key: string }
  | { entity: "shot"; key: string }
  | { entity: "slot"; id: number };

/** POST /:id/publish. Writes a snapshot of the live document and sets `publishedRevision`. */
export interface StoryboardPublishChangesInput {
  note?: string | null;
}

export interface StoryboardPublishChangesResult extends StoryboardEditResult {
  publishedRevision: number;
  snapshotId: number;
}

/* -------------------------------------------------------------------- undo */

/*
 * One mechanism undoes every storyboard edit: a new portal revision that
 * inverts the history rows of earlier ones, computed by the server from the
 * revision log; the browser sends only revision numbers. Redo is the same
 * call with the undo's own revision. It is guarded per field rather than by
 * `baseRevision`: refused only when somebody touched the same field or row
 * since, so an agent re-publish of other things never blocks it.
 */

/** At most this many revisions make one step (one focus session, one composite action). */
export const STORYBOARD_UNDO_MAX_REVISIONS = 20;

/** POST /:id/undo. Portal only, strategist or admin, the person's own revisions only. Sent without `baseRevision`. */
export interface StoryboardUndoInput {
  /** 1 to 20 distinct storyboard revision numbers forming one step; a redo sends the undo's own revision. */
  revisions: number[];
  /** Why, in a sentence, kept on the new revision's rows. */
  note?: string;
}

/** What POST /:id/undo answers on success. */
export interface StoryboardUndoResult {
  revision: number;
  /** False when the step was already undone (a double click, a retried request): nothing was written. */
  changed: boolean;
  /** The revisions whose rows this undo inverted, ascending. */
  undid: number[];
  /** True when any undone revision was already published: editors still see it until the next Publish changes. */
  published: boolean;
}

/**
 * The later change an undo would overwrite, carried in a 409 `undo_conflict`
 * (`blockers`, newest first) so the refusal can name what changed, who and
 * when.
 */
export interface StoryboardUndoBlocker {
  /** `storyboard` | `line` | `shot` | `slot`. */
  entity: string;
  /** `l03`, `s07`, `s07/frame/v2`, or the storyboard's slug. */
  entityKey: string | null;
  /** The field (`text`, `currentTakeId`, `meta.voiceSlotKey`); null for an archive, a create, a restore or an order. */
  field: string | null;
  /** The later row's action: `update` | `select_take` | `create` | `archive` | `restore` | `reorder`. */
  action: string;
  source: StoryboardChangeSource;
  by: { userId: number | null; name: string | null; viaAgent: boolean; you: boolean };
  at: string;
  storyboardRevision: number;
}

/* -------------------------------------------------------------------- save */

/*
 * The save bar (docs/ui-redesign/01-design.md, section 2.6). Every portal
 * edit is held as unsaved until its author saves or discards it. A person's
 * unsaved changes are their own portal revisions above `savedRevision` that
 * nobody (no other person, not the agent) has changed on top of since: a row
 * someone built on is no longer theirs alone, and goes out with whoever saves
 * next. Save publishes the whole live document to the editors; Discard
 * reverts only the caller's own rows.
 */

/** At most this many history rows in one discard; past it, save instead. */
export const STORYBOARD_DISCARD_MAX_ROWS = 2000;

/** Another person's unsaved changes, as the bar's second line names them. */
export interface StoryboardSaveStateOther {
  userId: number;
  name: string | null;
  /** One per revision: a change as the person thinks of it. */
  count: number;
  lastAt: string;
}

/** GET /:id/save-state. */
export interface StoryboardSaveState {
  revision: number;
  /** Everything at or below it is saved; null on a storyboard never saved or snapshotted. */
  savedRevision: number | null;
  publishedRevision: number | null;
  /** True once the concept is released to its editor: a save then reaches the editors at once. */
  released: boolean;
  /** The caller's unsaved changes; `since` is when the oldest was made. */
  mine: { count: number; since: string | null };
  others: StoryboardSaveStateOther[];
  /** The agent's revisions since the last save, which the next save sends out too; null when none. */
  agent: { count: number; lastAt: string } | null;
  /**
   * On a released concept with nobody mid-edit: revisions above the last save
   * that no active strategist holds (their person is disabled or gone, somebody
   * built on them, the agent made them, or they were unpublished when the save
   * bar arrived). Any Save sends them; the same rows are in `pending`, which
   * the bar shows and Discard takes back. Null when none.
   */
  waiting: { count: number; lastAt: string } | null;
  /**
   * Changes above the last save that no active person holds as their own
   * unsaved edits - the agent's, a script's, or those of somebody no longer
   * editing - on a storyboard the editors already have a version of. They
   * show in the one save bar like anyone's: Save sends them, Discard takes
   * them back to the published version. Never blocks leaving. Null when none.
   */
  pending: StoryboardSaveStatePending | null;
}

/** Who made the changes nobody holds, and what they are. */
export interface StoryboardSaveStatePending {
  /** One per revision. */
  count: number;
  lastAt: string;
  /** The people (or the agent) behind them, newest first. */
  by: Array<{ userId: number | null; name: string | null; agent: boolean }>;
  /** What changed, one entry per changed thing, newest first (at most 20), in the shape the history names changes. */
  changes: StoryboardUndoBlocker[];
}

/** POST /:id/save. */
export interface StoryboardSaveInput {
  /** The `revision` the caller's save-state read; another person's or the agent's revision after it is a 409 `save_moved`. */
  knownRevision: number;
}

export interface StoryboardSaveResult {
  revision: number;
  savedRevision: number;
  publishedRevision: number;
  /** Other people's unsaved changes this save sent out with the caller's. */
  includedOthers: StoryboardSaveStateOther[];
  /** The agent's revisions this save sent out; null when none. */
  includedAgent: { count: number; lastAt: string } | null;
}

/** POST /:id/discard. */
export interface StoryboardDiscardResult {
  revision: number;
  /** The revisions whose rows were reverted, ascending; empty when there was nothing to discard. */
  undid: number[];
  /** What stayed because somebody changed it since, newest first, each naming who and when. */
  kept: StoryboardUndoBlocker[];
}

/**
 * A call step's refusal (voices, library images): the row moved since the
 * step's forward answer (`ifUpdatedAt`). Gallery hides are guarded by
 * `hide_current` instead and never answer one.
 */
export interface UndoCallBlocker {
  what: "voice" | "library_image";
  by: { userId: number | null; name: string | null; you: boolean };
  at: string;
}
